// One playbook the agent proposed from real chats (agent v2 `playbooks`,
// migration 279) with the owner's three buttons: Valider / Modifier / Refuser.
// Modifier opens the guidance inline; « Enregistrer et valider » approves with
// his wording (an approved one keeps going with the new text at once). The
// worker refuses a text that mentions a refund, a discount or « IA ».
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, type PlaybookItem } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { whenLabel } from './agentActionsView';
import { casesLine, PLAYBOOK_STATUS_LABEL, PLAYBOOK_STATUS_TONE, tagLabel, usesLine } from './playbooksView';

/** Every playbook list shares this key, so one decision refreshes them all. */
export const PLAYBOOKS_KEY = 'agent-v2-playbooks';

type Act = { kind: 'approve'; text?: string | null } | { kind: 'edit'; text: string } | { kind: 'reject' };

export function PlaybookRow({ item }: { item: PlaybookItem }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.guidance);
  const [msg, setMsg] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (a: Act) => (a.kind === 'approve'
      ? whatsappApi.approvePlaybook(item.id, a.text ?? null)
      : a.kind === 'edit' ? whatsappApi.editPlaybook(item.id, a.text) : whatsappApi.rejectPlaybook(item.id)),
    onSuccess: () => {
      setMsg(null);
      setEditing(false);
      void qc.invalidateQueries({ queryKey: [PLAYBOOKS_KEY] });
    },
    onError: (e: Error) => setMsg(e.message),
  });
  const open = item.status !== 'rejected';
  const actions = Array.isArray(item.actions) ? item.actions.filter(Boolean) : [];
  const cases = casesLine(item);
  const uses = usesLine(item);
  const changed = draft.trim() !== item.guidance.trim();

  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3.5 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-gray-800 break-words">{item.situation}</span>
            <span className={`text-[10.5px] font-semibold rounded-full border px-2 py-0.5 ${PLAYBOOK_STATUS_TONE[item.status]}`}>
              {t(PLAYBOOK_STATUS_LABEL[item.status])}
            </span>
            <span className="text-[10.5px] rounded-full border border-gray-200 bg-gray-50 text-gray-600 px-2 py-0.5">
              {t(tagLabel(item.situation_tag))}
            </span>
          </div>

          {editing ? (
            <div className="mt-2 space-y-2">
              <label className="block text-[11px] text-gray-500" htmlFor={`pb-${item.id}`}>{t('Comment répondre')}</label>
              <textarea id={`pb-${item.id}`} value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} maxLength={1200}
                className="w-full rounded-md border border-gray-200 px-2.5 py-2 text-[12.5px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/40" />
              <div className="flex gap-1.5 flex-wrap">
                {item.status === 'proposed' ? (
                  <button type="button" disabled={run.isPending || draft.trim().length < 3}
                    onClick={() => run.mutate({ kind: 'approve', text: changed ? draft.trim() : null })}
                    className="h-8 px-3 rounded-md bg-brand-500 text-white text-[12px] font-semibold cursor-pointer disabled:opacity-50">
                    {t('Enregistrer et valider')}
                  </button>
                ) : (
                  <button type="button" disabled={run.isPending || !changed || draft.trim().length < 3}
                    onClick={() => run.mutate({ kind: 'edit', text: draft.trim() })}
                    className="h-8 px-3 rounded-md bg-brand-500 text-white text-[12px] font-semibold cursor-pointer disabled:opacity-50">
                    {t('Enregistrer')}
                  </button>
                )}
                <button type="button" disabled={run.isPending} onClick={() => { setEditing(false); setDraft(item.guidance); setMsg(null); }}
                  className="h-8 px-3 rounded-md border border-gray-200 text-[12px] text-gray-700 cursor-pointer disabled:opacity-50">
                  {t('Annuler')}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[12.5px] text-gray-700 mt-1 leading-snug break-words">
              <span className="text-gray-400">{t('Réponse :')} </span>{item.guidance}
              {item.edited_text && <span className="text-[11px] text-gray-400"> ({t('modifié par vous')})</span>}
            </p>
          )}

          {actions.length > 0 && (
            <p className="text-[11.5px] text-gray-600 mt-1 break-words">
              <span className="text-gray-400">{t('Actions :')} </span>{actions.join(' · ')}
            </p>
          )}
          <p className="text-[11px] text-gray-400 mt-0.5 tabular-nums">
            {[whenLabel(item.created_at), cases, uses ?? '', item.decided_by && item.status !== 'proposed' ? `${t('par')} ${item.decided_by}` : '']
              .filter(Boolean).join(' · ')}
          </p>
        </div>

        {open && !editing && (
          <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
            {item.status === 'proposed' && (
              <button type="button" disabled={run.isPending} onClick={() => run.mutate({ kind: 'approve' })}
                className="h-8 px-3 rounded-md bg-brand-500 text-white text-[12px] font-semibold cursor-pointer disabled:opacity-50">
                {t('Valider')}
              </button>
            )}
            <button type="button" disabled={run.isPending} onClick={() => setEditing(true)}
              className="h-8 px-3 rounded-md border border-gray-200 text-[12px] text-gray-700 cursor-pointer disabled:opacity-50">
              {t('Modifier')}
            </button>
            <button type="button" disabled={run.isPending}
              onClick={() => { if (item.status === 'proposed' || window.confirm(t('Retirer ce playbook ? L’agent ne s’en servira plus.'))) run.mutate({ kind: 'reject' }); }}
              className="h-8 px-3 rounded-md border border-gray-200 text-[12px] text-gray-700 cursor-pointer disabled:opacity-50">
              {item.status === 'approved' ? t('Retirer') : t('Refuser')}
            </button>
          </div>
        )}
      </div>
      {msg && <p className="text-[11.5px] text-amber-700 mt-1.5" role="alert">{msg}</p>}
    </div>
  );
}
